import React, { useEffect, useState } from 'react';
import { MdClose, MdContentCopy, MdBusiness, MdLanguage, MdCategory, MdTrendingUp } from 'react-icons/md';
import { services } from '../../services/api';

const InstrumentProfileModal = ({ isOpen, onClose, symbol, exchange }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && symbol) {
      fetchProfile();
    } else {
        setProfile(null);
    }
  }, [isOpen, symbol]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await services.instrumentMaster.getProfile(symbol, exchange);
      
      // Axios returns full response object, data is in response.data
      const profileData = response.data || response;
      
      if (profileData && profileData.symbol) {
        setProfile(profileData);
      } else {
        setError("Profile not found");
      }
    } catch (err) {
      console.error(err);
      // Check if it's a rate limit error (429)
      if (err.response?.status === 429) {
        setError("⏱️ Yahoo Finance rate limit exceeded. Please wait a moment and try again.");
      } else {
        setError(err.response?.data?.detail || err.message || "Failed to load profile");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-[#1A1D23] w-full max-w-2xl rounded-2xl shadow-2xl border border-[var(--divider)] overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="p-5 border-b border-[var(--divider)] flex items-center justify-between sticky top-0 bg-white dark:bg-[#1A1D23] z-10">
           <div className="flex items-center gap-4">
              {profile?.logo_url ? (
                  <img src={profile.logo_url} alt="Logo" className="w-12 h-12 object-contain bg-white rounded-lg p-1 border border-[var(--divider)]" />
              ) : (
                  <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg flex items-center justify-center text-indigo-500 font-black text-xl">
                      {symbol?.charAt(0)}
                  </div>
              )}
              <div>
                  <h2 className="ds-h2 text-fg-1 tracking-tight">
                    {profile?.short_name || symbol}
                  </h2>
                  <div className="flex items-center gap-2 mt-1">
                      <span className="ds-badge sm neutral">
                          {exchange}
                      </span>
                      {profile?.sector && (
                          <span className="ds-badge sm info">
                              {profile.sector}
                          </span>
                      )}
                  </div>
              </div>
           </div>
           
           <button 
             onClick={onClose}
             className="p-2 rounded-xl hover:bg-bg-3 text-fg-3 hover:text-fg-2 dark:hover:text-gray-200 transition-colors"
           >
             <MdClose size={24} />
           </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto custom-scrollbar">
           {loading ? (
               <div className="flex flex-col items-center justify-center py-12 space-y-4">
                   <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                   <p className="text-sm font-medium text-fg-3 animate-pulse">Fetching Profile...</p>
               </div>
           ) : error ? (
               <div className="py-12 text-center">
                   <p className="text-red-500 font-medium bg-red-50 dark:bg-red-900/10 py-3 px-4 rounded-xl inline-block">
                       {error}
                   </p>
               </div>
           ) : (
               <div className="space-y-8">
                   {/* Description */}
                   <div className="prose dark:prose-invert max-w-none">
                       <p className="text-sm leading-relaxed text-fg-2">
                           {profile.description || "No description available."}
                       </p>
                   </div>

                   {/* Key Stats Grid */}
                   <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                       {profile.industry && (
                           <div className="p-4 rounded-xl bg-bg-3 dark:bg-[#20232a] border border-[var(--divider)]">
                               <div className="flex items-center gap-2 mb-1 text-fg-3">
                                   <MdCategory size={16} />
                                   <span className="text-xs font-bold uppercase tracking-wider">Industry</span>
                               </div>
                               <p className="font-semibold text-fg-1 text-sm truncate" title={profile.industry}>
                                   {profile.industry}
                               </p>
                           </div>
                       )}

                       {profile.website && (
                           <a 
                             href={profile.website} 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="p-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 transition-colors group"
                            >
                               <div className="flex items-center gap-2 mb-1 text-indigo-400">
                                   <MdLanguage size={16} />
                                   <span className="text-xs font-bold uppercase tracking-wider">Website</span>
                               </div>
                               <p className="font-semibold text-indigo-600 dark:text-indigo-400 text-sm truncate group-hover:underline">
                                   Visit Site
                               </p>
                           </a>
                       )}
                       
                        {profile.market_cap && (
                           <div className="p-4 rounded-xl bg-bg-3 dark:bg-[#20232a] border border-[var(--divider)]">
                               <div className="flex items-center gap-2 mb-1 text-fg-3">
                                   <MdTrendingUp size={16} />
                                   <span className="text-xs font-bold uppercase tracking-wider">Market Cap</span>
                               </div>
                               <p className="font-semibold text-fg-1 text-sm">
                                   {(profile.market_cap / 10000000).toFixed(2)} Cr
                               </p>
                           </div>
                       )}
                   </div>
               </div>
           )}
        </div>
      </div>
    </div>
  );
};

export default InstrumentProfileModal;
